import pkg from '../../package.json';
import Dinero from 'dinero.js';
import bwip from 'bwip-js';

import date from '../filters/date';
import practiceLabel from '../filters/practice';

function cm(value) {
  return Math.floor(value * 28);
}

function tar(text) {
  return { text, style: 'td', alignment: 'right' };
}

function val(value) {
  return value || '';
}

function has(values, value) {
  if (typeof values !== 'array') return 'Non';
  return values.indexOf(value) >= 0 ? 'Oui' : 'Non';
}

function practiceAddress(practice) {
  return [practice.addressStreet, practice.postalCode, practice.addressLocality]
    .filter(v => v)
    .join(' ');
}

function stringMin(a, b) {
  if (!a && !b) return '';
  else if (!a) return a;
  else if (!b) return b;
  return a.localeCompare(b) < 0 ? a : b;
}

function stringMax(a, b) {
  if (!a && !b) return '';
  else if (!a) return a;
  else if (!b) return b;
  return a.localeCompare(b) > 0 ? a : b;
}

function treatmentDateSpan(invoice) {
  if (!invoice.lines.length) return '';
  let min = invoice.lines
    .map(l => l.date)
    .reduce((min, date) => stringMin(min, date));
  let max = invoice.lines
    .map(l => l.date)
    .reduce((min, date) => stringMax(min, date));
  return date(min) + ' - ' + date(max);
}

function amount(value, format = '0.00') {
  return Dinero(value).toFormat(format);
}

function percent(value) {
  if (!value) return '0.0%';
  return value * 100 + '%';
}

function patientCompany(patient) {
  return [patient.companyNumber, patient.companyName].filter(v => v).join(' ');
}

function address(address) {
  if (address.addressCustom) {
    return address.addressCustomContent;
  } else {
    return [
      [address.givenName, address.familyName].filter(v => v).join(' '),
      address.addressStreet,
      [address.postalCode, address.addressLocality].filter(v => v).join(' ')
    ]
      .filter(v => v)
      .join('\n');
  }
}

function postCode(invoice) {
  //  var pNum = request.Physio.TPW_Move.Leistungen.total.FB_Int.rawValue;
  var moduleTable = [0, 9, 4, 6, 8, 2, 7, 1, 3, 5];

  let amountString = Dinero(invoice.totalDue)
    .convertPrecision(2)
    .toObject()
    .amount.toString();
  let amount = '01' + amountString.padStart(10, '0');
  let carry = 0;

  // amount
  for (var i = 0; i < amount.length; i++) {
    carry = moduleTable[(carry + parseInt(amount.charAt(i))) % 10];
  }
  var digit1 = (10 - carry) % 10;

  // identification
  var rnr = (invoice.number || '').replace(/[^\d]/g, '').padStart(20, '0');
  let id = '0'.padStart(6, '0');
  var idRnr = id + rnr.substr(0, 20);

  carry = 0;
  for (var i = 0; i < idRnr.length; i++) {
    carry = moduleTable[(carry + parseInt(idRnr.charAt(i))) % 10];
  }
  let digit2 = (10 - carry) % 10;

  let account = '';
  if (invoice.issuer && invoice.issuer.postalAccount) {
    account = invoice.issuer.postalAccount
      .replace(/[^\d]/g, '')
      .padStart(9, '0');
  }
  return amount + digit1 + '>' + idRnr + digit2 + '+ ' + account + '>';
}

function barCodeGenerate(invoice) {
  return new Promise(function(resolve, reject) {
    let canvas = document.createElement('canvas');
    // let canvas = new OffscreenCanvas(20, 20);
    let options = {
      bcid: 'datamatrix',
      text: invoice.barcode || 'draft',
      scale: 1,
      height: 20,
      width: 20
    };
    bwip(canvas, options, function(err, cvs) {
      if (err) return reject(err);
      resolve(cvs.toDataURL('image/png'));
    });
  });
}

export default async function(invoice, i18n, locale, uid) {
  if (!invoice) return { doc: {} };

  locale = locale || i18n.current;

  let issuer = invoice.issuer || {};
  let provider = invoice.noProvider ? issuer : invoice.provider || {};
  let patient = invoice.patient || {};

  function t(key, ...args) {
    const label = 'invoice_pdf_' + key;
    return i18n ? i18n.$t(label, ...args) : label;
  }

  function tc(key, ...args) {
    return i18n.$tc('invoice_pdf_' + key, ...args);
  }

  function gt(group, key, ...args) {
    if (!key) return '';
    return i18n.$t(group + key, ...args);
  }

  function background(invoice, uid) {
    if (!uid) {
      return [
        {
          text: t('banner_trial_title'),
          style: 'banner',
          margin: [56, 28 * 2, 0, 0]
        },
        {
          text: t('banner_trial_subtitle'),
          style: 'banner2',
          margin: [65, 0, 0, 0]
        }
      ];
    } else if (invoice.status == 'draft') {
      return [
        {
          text: t('banner_draft_title'),
          style: 'banner-draft',
          margin: [56, 28 * 2, 0, 0]
        },
        {
          text: t('banner_draft_subtitle'),
          style: 'banner2-draft',
          margin: [65, 0, 0, 0]
        }
      ];
    } else {
      return [];
    }
  }

  const barCode = await barCodeGenerate(invoice);

  let patientWitdthsBase = [cm(2.6), cm(2.8), '*'];
  let patientWitdthsAddress = [cm(2.6), cm(2.8), cm(5.7), cm(5.5), '*']; // 11.1 + 5.5
  let patientWitdthsTwoColumns = [cm(2.6), cm(2.8), cm(4.6), cm(3.1), '*'];

  const copies = invoice.copies
    ? [...invoice.copies].sort((a, b) => b.localeCompare(a))
    : [];
  const sections = ['insurer', ...copies];

  function bodyVariable(law) {
    switch (law) {
      case 'lamal':
      case 'lca':
        return [
          [
            '',
            { text: t('patient_case_date'), style: 'td' },
            { text: date(patient.caseDate), style: 'td' }
          ],
          [
            '',
            { text: t('patient_patient_number'), style: 'td' },
            { text: val(patient.patientNumber), style: 'td' }
          ],
          [
            '',
            { text: t('patient_avs_number'), style: 'td' },
            { text: val(patient.avsNumber), style: 'td' }
          ],
          [
            '',
            { text: t('patient_cada_number'), style: 'td' },
            { text: val(patient.cadaNumber), style: 'td' }
          ],
          [
            '',
            { text: t('patient_insured_number'), style: 'td' },
            { text: val(patient.insuredNumber), style: 'td' }
          ]
        ];
      case 'laa':
        return [
          [
            '',
            { text: t('patient_case_date'), style: 'td' },
            { text: date(patient.caseDate), style: 'td' }
          ],
          [
            '',
            { text: t('patient_case_number'), style: 'td' },
            { text: val(patient.caseNumber), style: 'td' }
          ],
          [
            '',
            { text: t('patient_avs_number'), style: 'td' },
            { text: val(patient.avsNumber), style: 'td' }
          ],
          [
            '',
            { text: t('patient_cada_number'), style: 'td' },
            { text: val(patient.cadaNumber), style: 'td' }
          ],
          ['', '', '']
        ];
      case 'lai':
        return [
          [
            '',
            { text: t('patient_decision_date'), style: 'td' },
            { text: date(patient.decisionDate), style: 'td' }
          ],
          [
            '',
            { text: t('patient_decision_number'), style: 'td' },
            { text: val(patient.decisionNumber), style: 'td' }
          ],
          [
            '',
            { text: t('patient_avs_number'), style: 'td' },
            { text: val(patient.avsNumber), style: 'td' }
          ],
          [
            '',
            { text: t('patient_cada_number'), style: 'td' },
            { text: val(patient.cadaNumber), style: 'td' }
          ],
          ['', '', '']
        ];
      case 'lam':
      case 'co':
        return [
          [
            '',
            { text: t('patient_case_date'), style: 'td' },
            { text: date(patient.caseDate), style: 'td' }
          ],
          [
            '',
            { text: t('patient_patient_number'), style: 'td' },
            { text: val(patient.patientNumber), style: 'td' }
          ],
          [
            '',
            { text: t('patient_avs_number'), style: 'td' },
            { text: val(patient.avsNumber), style: 'td' }
          ],
          [
            '',
            { text: t('patient_cada_number'), style: 'td' },
            { text: val(patient.cadaNumber), style: 'td' }
          ],
          ['', '', '']
        ];
      default:
        return [];
    }
  }

  function body(section) {
    const pageBreak = sections.lastIndexOf(section) < sections.length - 1;
    return [
      {
        layout: 'patientTop',
        table: {
          headerRows: 0,
          widths: patientWitdthsBase,
          body: [
            [
              { text: t('patient_label'), style: 'th' },
              { text: t('patient_family_name'), style: 'td' },
              { text: val(patient.familyName), style: 'td' }
            ],
            [
              '',
              { text: t('patient_given_name'), style: 'td' },
              { text: val(patient.givenName), style: 'td' }
            ],
            [
              '',
              { text: t('patient_street_name'), style: 'td' },
              { text: val(patient.addressStreet), style: 'td' }
            ],
            [
              '',
              { text: t('patient_postal_code'), style: 'td' },
              { text: val(patient.postalCode), style: 'td' }
            ],
            [
              '',
              { text: t('patient_locality'), style: 'td' },
              { text: val(patient.addressLocality), style: 'td' }
            ],
            [
              '',
              { text: t('patient_birth_date'), style: 'td' },
              { text: date(patient.birthDate), style: 'td' }
            ],
            [
              '',
              { text: t('patient_gender'), style: 'td' },
              {
                text: gt('invoice_pdf_patient_gender_', patient.gender),
                style: 'td'
              }
            ],
            ...bodyVariable(patient.law),
            [
              '',
              { text: t('patient_canton'), style: 'td' },
              { text: val(patient.canton).toUpperCase(), style: 'td' }
            ],
            [
              '',
              { text: t('patient_invoice_copy'), style: 'td' },
              { text: has(invoice.copies, 'patient'), style: 'td' }
            ]
          ]
        }
      },
      {
        layout: 'patientMiddle',
        table: {
          headerRows: 0,
          widths: patientWitdthsTwoColumns,
          body: [
            [
              '',
              { text: t('patient_refund_type'), style: 'td' },
              {
                text: gt('patient_refund_type_', patient.refundType),
                style: 'td'
              },
              { text: t('patient_gaprch'), style: 'td' },
              {
                text: [
                  date(invoice.gaprchDate),
                  ' ',
                  val(invoice.gaprchNumber)
                ],
                style: 'td'
              }
            ],
            [
              '',
              { text: t('patient_law'), style: 'td' },
              { text: gt('patient_law_', patient.law), style: 'td' },
              { text: t('number'), style: 'td' },
              {
                text: [date(invoice.date), ' ', val(invoice.number)],
                style: 'td'
              }
            ],
            [
              '',
              { text: t('patient_contract_number'), style: 'td' },
              { text: val(patient.contractNumber) },
              { text: t('reminder'), style: 'td' },
              {
                text: [
                  date(invoice.reminderDate),
                  ' ',
                  val(invoice.reminderNumber)
                ],
                style: 'td'
              }
            ],
            [
              '',
              { text: t('patient_treatment'), style: 'td' },
              { text: treatmentDateSpan(invoice), style: 'td' },
              { text: t('patient_treatment_reason'), style: 'td' },
              { text: gt('invoice_reason_', invoice.reason), style: 'td' }
            ]
          ]
        }
      },
      {
        text: address(patient),
        style: 'td',
        absolutePosition: { x: cm(11.6), y: cm(5.5) },
        width: cm(5.5)
      },
      {
        layout: 'patientBottom', // optional
        table: {
          headerRows: 0,
          widths: [cm(2.6), cm(2.8), cm(3.9), cm(1.0), cm(2), cm(5.5), '*'],
          body: [
            // two columns
            [
              '',
              { text: t('patient_role'), style: 'td' },
              { text: t('patient_role_mc'), style: 'td' },
              '',
              '',
              '',
              ''
            ], // TODO note sure about this one
            [
              '',
              { text: t('patient_company'), style: 'td' },
              { text: patientCompany(patient), style: 'td' },
              '',
              '',
              '',
              ''
            ], // TODO neither about this one

            [
              { text: t('mandatary'), style: 'th' },
              { text: t('mandatary_label'), style: 'td' },
              {
                text: [
                  val(invoice.mandataryGln),
                  val(invoice.mandataryRcc),
                  val(invoice.mandataryName)
                ].join(' / '),
                style: 'td'
              },
              '',
              '',
              '',
              ''
            ],
            [
              { text: t('diagnostic'), style: 'th' },
              {
                text: [
                  gt('invoice_diagnostic_', invoice.diagnosticType),
                  ' ',
                  val(invoice.diagnosticText)
                ],
                style: 'td',
                colSpan: 6
              },
              '',
              '',
              '',
              '',
              ''
            ],
            [
              { text: t('therapy'), style: 'th' },
              {
                text: gt('therapy_', invoice.therapy),
                style: 'td',
                colSpan: 6
              },
              '',
              '',
              '',
              '',
              ''
            ],
            [
              { text: t('comments'), style: 'th' },
              { text: val(invoice.note), style: 'td', colSpan: 6 },
              '',
              '',
              '',
              '',
              ''
            ]
          ]
        }
      },
      {
        layout: 'lines',
        pageBreak: pageBreak ? 'after' : undefined,
        margin: [0, 14, 0, 0],
        table: {
          style: 'td',
          headerRows: 1,
          widths: [61, 20, 140, 36, 59, '*', 42, 50],
          body: [
            [
              { text: t('line_date'), style: 'td' },
              { text: t('line_tarif'), style: 'td' },
              { text: t('line_code'), style: 'td' },
              {
                text: t('line_quantity'),
                styles: ['td', 'r'],
                style: 'td',
                alignment: 'right'
              },
              {
                text: t('line_price'),
                styles: ['td', 'r'],
                style: 'td',
                alignment: 'right'
              },
              {
                text: t('line_point_value'),
                styles: ['td', 'r'],
                style: 'td',
                alignment: 'right'
              },
              {
                text: t('line_point_vat'),
                styles: ['td', 'r'],
                style: 'td',
                alignment: 'right'
              },
              {
                text: t('line_point_amount'),
                styles: ['td', 'r'],
                style: 'td',
                alignment: 'right'
              }
            ],
            ...invoice.lines.flatMap(l => [
              [
                { text: date(l.date), style: 'td' }, //, style: 'td'}
                { text: val(l.tarif), style: 'td' },
                { text: val(l.code) || val(l.label), style: 'td' },
                { text: val(l.quantity), style: 'td', alignment: 'right' },
                {
                  text: amount(l.unitPrice, '0.0000'),
                  style: 'td',
                  alignment: 'right'
                },
                { text: '1.00', style: 'td', alignment: 'right' },
                { text: percent(l.vatRate), style: 'td', alignment: 'right' },
                { text: amount(l.amount), style: 'td', alignment: 'right' }
              ],
              [
                '',
                '',
                {
                  text:
                    l.tarif === '999'
                      ? val(l.description)
                      : l.label18
                      ? val(l.label18.fr)
                      : '',
                  style: 'td',
                  colSpan: 6,
                  bold: true
                }
              ]
            ])
          ]
        }
      }
    ];
  }

  const doc = {
    // compress: false,
    pageSize: 'A4',
    background: background(invoice, uid),
    pageOrientation: 'portrait',
    pageMargins: [28, 99, 28, 28 * 4.2], // 28 pica = 1cm, 72 pica = 1 inch
    info: {
      title: t('info_title', invoice),
      author: 'Simple590',
      subject: '',
      keywords: '',
      creator: 'Simple590.ch',
      producer: 'Simple590.ch',
      creationDate: '', // TODO invoice creation date
      modDate: '', // TOOD
      simple590: JSON.stringify(invoice)
      // trapped: ''
    },

    styles: {
      banner: {
        fontSize: 64,
        color: 'lightgrey'
      },
      banner2: {
        fontSize: 20,
        color: 'lightgrey'
      },
      'banner-draft': {
        fontSize: 128,
        color: 'lightgrey'
      },
      'banner2-draft': {
        fontSize: 36,
        color: 'lightgrey'
      },
      header: {
        fontSize: 12,
        // bold: true,
        color: 'black'
      },
      release: {
        fontSize: 9,
        color: 'black'
      },
      th: {
        fontSize: 8,
        // bold: true,
        color: 'black'
      },
      td: {
        fontSize: 8,
        color: 'black'
      },
      r: {
        alignment: 'right'
      },
      postcode: {
        textSize: 10,
        color: 'black'
      }
    },
    defaultStyle: {
      fontSize: 9,
      font: 'Helvetica',
      color: 'red'
    },

    header: function(currentPage, totalPage, pageSize) {
      const sectionNum = Math.floor(
        ((currentPage - 1) / totalPage) * sections.length
      );
      const section = sections[sectionNum];
      const title = t([section, 'title', invoice.patient.refundType].join('_'));
      return {
        margin: [28, 28, 28, 0],
        stack: [
          {
            columnGap: 0,
            columns: [
              {
                text: title,
                style: 'header',
                width: '80%',
                margin: [0, 0, 0, 6]
              },
              {
                text: t('release', pkg),
                style: 'release',
                alignment: 'right',
                width: '20%',
                margin: [0, 6, 0, 0]
              }
            ]
          },
          {
            layout: 'practices', // optional
            table: {
              // headers are automatically repeated if the table spans over multiple pages
              // you can declare how many rows should be treated as headers
              headerRows: 0,
              widths: [cm(1.8), cm(1.7), cm(2.5), '*', cm(1), cm(4.5)],

              body: [
                [
                  { text: t('header_document'), style: 'th' },
                  {
                    text: [t('header_id'), ' ', val(invoice.number)],
                    style: 'td',
                    colSpan: 3
                  },
                  '',
                  {
                    text: t('header_page', { currentPage, totalPage }),
                    style: 'td',
                    alignment: 'right',
                    colSpan: 2
                  },
                  '',
                  ''
                ],
                [
                  { text: t('header_issuer_label1'), style: 'th' },
                  { text: t('header_issuer_gln'), style: 'td' },
                  { text: val(issuer.glnNumber), style: 'td' },
                  { text: practiceLabel(issuer), style: 'td' },
                  {
                    text: issuer.email ? t('header_issuer_email') : '',
                    style: 'td'
                  },
                  { text: val(issuer.email), style: 'td' }
                ],
                [
                  { text: t('header_issuer_label2'), style: 'th' },
                  { text: t('header_issuer_rcc'), style: 'td' },
                  { text: val(issuer.rccNumber), style: 'td' },
                  { text: practiceAddress(issuer), style: 'td' },
                  {
                    text: issuer.telephone ? t('header_issuer_telephone') : '',
                    style: 'td'
                  },
                  { text: val(issuer.telephone), style: 'td' }
                ],
                [
                  { text: t('header_provider_label1'), style: 'th' },
                  { text: t('header_provider_gln'), style: 'td' },
                  { text: val(provider.glnNumber), style: 'td' },
                  { text: practiceLabel(provider), style: 'td' },
                  {
                    text: provider.email ? t('header_provider_email') : '',
                    style: 'td'
                  },
                  { text: val(provider.email), style: 'td' }
                ],
                [
                  { text: t('header_provider_label2'), style: 'th' },
                  { text: t('header_provider_rcc'), style: 'td' },
                  { text: val(provider.rccNumber), style: 'td' },
                  { text: practiceAddress(provider), style: 'td' },
                  {
                    text: provider.telephone
                      ? t('header_provider_telephone')
                      : '',
                    style: 'td'
                  },
                  { text: val(provider.telephone), style: 'td' }
                ]
              ]
            }
          }
        ]
      };
    },

    content: [...sections.map(body)],

    footer: function(currentPage, pageCount, pageSize) {
      return [
        {
          margin: [28, 28, 28, 0],
          layout: 'zero',
          table: {
            widths: [cm(6), cm(8.8), '*'],
            body: [
              [
                {
                  layout: 'none',
                  table: {
                    headerRows: 0,
                    widths: [cm(0.9), cm(0.9), cm(1.5), cm(1)],
                    body: [
                      [
                        {
                          text: t('vat_code'),
                          style: 'td',
                          alignment: 'center'
                        },
                        {
                          text: t('vat_rate'),
                          style: 'td',
                          alignment: 'center'
                        },
                        {
                          text: t('vat_amount'),
                          style: 'td',
                          alignment: 'right'
                        },
                        { text: t('vat_vat'), style: 'td', alignment: 'right' }
                      ],
                      ...(invoice.vatRates || []).map(line => [
                        { text: line.code, style: 'td', alignment: 'center' },
                        {
                          text: percent(line.rate),
                          style: 'td',
                          alignment: 'center'
                        },
                        {
                          text: amount(line.totalEot),
                          style: 'td',
                          alignment: 'right'
                        },
                        {
                          text: amount(line.totalVat),
                          style: 'td',
                          alignment: 'right'
                        }
                      ])
                    ]
                  }
                },
                {
                  layout: 'none',
                  table: {
                    widths: [cm(1.5), cm(3), cm(2), '*'],
                    body: [
                      [
                        { text: t('footer_vat'), style: 'td' },
                        { text: val(issuer.vatNumber), style: 'td' },
                        '',
                        ''
                      ],
                      [
                        { text: t('footer_currency'), style: 'td' },
                        { text: 'CHF', style: 'td' },
                        '',
                        ''
                      ],
                      [
                        { text: t('footer_iban'), style: 'td' },
                        { text: val(issuer.iban), style: 'td', colSpan: 3 }
                      ],
                      [
                        {
                          text: tc(
                            'footer_payment_delay',
                            issuer.paymentDelay,
                            { days: issuer.paymentDelay }
                          ),
                          style: 'td',
                          colSpan: 2,
                          bold: true
                        },
                        '',
                        { text: t('footer_deposit'), style: 'td' },
                        {
                          text: amount(invoice.deposit),
                          style: 'td',
                          alignment: 'right'
                        }
                      ]
                    ]
                  }
                },
                {
                  layout: 'none',
                  table: {
                    widths: [Math.round(2.6 * 26) - 6, 28 * 2 - 6],
                    body: [
                      [
                        { text: t('footer_total'), style: 'td' },
                        {
                          text: amount(invoice.totalTi),
                          style: 'td',
                          alignment: 'right'
                        }
                      ],
                      [{ text: ' ', style: 'td' }, ''],
                      [{ text: ' ', style: 'td' }, ''],
                      [
                        { text: t('footer_due'), style: 'td' },
                        {
                          text: amount(invoice.totalDue),
                          style: 'td',
                          alignment: 'right'
                        }
                      ]
                    ]
                  }
                }
              ]
            ]
          }
        },
        {
          margin: [28, 0, 28, 0],
          columns: [
            { image: barCode, width: cm(0.8), height: cm(0.8) },
            //            '/-/#00#00000000#00000000000000',
            {
              text: postCode(invoice),
              style: 'postcode',
              alignment: 'right',
              margin: [0, 3, 0, 0]
            }
          ]
        }
      ];
    }
  };

  const tableLayouts = {
    practices: {
      hLineWidth(i, node) {
        return i === 0 || i === node.table.body.length ? 0.5 : 0;
      },
      vLineWidth(i, node) {
        return i === 0 || i === node.table.widths.length ? 0.5 : 0;
      },
      hLineColor(i, node) {
        return 'black';
      },
      vLineColor(i, node) {
        return 'black';
      },
      paddingLeft(i, node) {
        return 3;
      },
      paddingTop(i, node) {
        return [3, 3, 0, 3, 0][i] || 0;
      },
      paddingRight(i, node) {
        return 3;
      },
      paddingBottom(i, node) {
        return [0, 0, 0, 0, 3][i] || 0;
      }
    },
    patientTop: {
      hLineWidth: (i, node) => 0,
      vLineWidth: (i, node) =>
        i === 0 || i === node.table.widths.length ? 0.5 : 0,
      hLineColor: i => 'black',
      vLineColor: i => 'black',
      paddingTop: i => 1,
      paddingBottom: i => 0,
      paddingLeft: i => (i === 0 ? 3 : 0),
      paddingRight: (i, node) => 0
    },
    patientMiddle: {
      hLineWidth: (i, node) => 0,
      vLineWidth: (i, node) =>
        i === 0 || i === node.table.widths.length ? 0.5 : 0,
      hLineColor: i => 'black',
      vLineColor: i => 'black',
      paddingTop: i => 1,
      paddingBottom: i => 0,
      paddingLeft: i => (i === 0 ? 3 : 0),
      paddingRight: (i, node) => 0
    },
    patientBottom: {
      hLineWidth: (i, node) => (i > 1 ? 0.5 : 0),
      vLineWidth: (i, node) =>
        i === 0 || i === node.table.widths.length ? 0.5 : 0,
      hLineColor: i => 'black',
      vLineColor: i => 'black',
      paddingTop: i => 1,
      paddingBottom: i => 0,
      paddingLeft: i => (i === 0 ? 3 : 0),
      paddingRight: (i, node) => 0
    },
    lines: {
      hLineWidth: (i, node) => 0,
      vLineWidth: (i, none) => 0,
      paddingTop: i => (i % 2 ? 3 : 0),
      paddingBottom: i => 3,
      paddingLeft: i => (i === 0 ? 3 : 0),
      paddingRight: (i, node) => 0
    },
    none: {
      hLineWidth: (i, node) => 0,
      vLineWidth: (i, none) => 0
    },
    zero: {
      hLineWidth: (i, node) => 0,
      vLineWidth: (i, none) => 0,
      paddingTop: i => 0,
      paddingBottom: i => 0,
      paddingLeft: i => 0,
      paddingRight: i => 0
    }
  };

  return { doc, tableLayouts };
}
